import React from 'react';
import styled from '@emotion/styled';
import { Color, FontSize, MAX_WIDTH } from 'views/constants';
import { Typography } from 'views/components/atoms/Typography';
import { Spacer } from 'views/components/atoms/Spacer';
import { Action } from 'models/Dialog/type';
import { localeIds } from 'locals';
import { FormattedMessage } from 'react-intl';

type WrapperProp = {
  shown: boolean;
};

const Wrapper = styled.div<WrapperProp>`
  position: fixed;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  z-index: 2000;
  display: ${({ shown }) => (shown ? 'block' : 'none')};
`;

const Layout = styled.div`
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-x: hidden;
  overflow-y: auto;
  backface-visibility: hidden;
  padding: 60px 0px 30px;
`;

const Overlay = styled.button`
  position: fixed;
  top: 0px;
  right: 0px;
  bottom: 0px;
  left: 0px;
  z-index: 3;
  width: 100%;
  height: 100%;
  will-change: opacity;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
  transition-property: opacity;
  background-color: rgba(0, 0, 0, 0.5);
  border-width: initial;
  border-style: none;
  border-color: initial;
  border-image: initial;
`;

const Content = styled.div`
  position: relative;
  z-index: 4;
  margin: auto 24px;
  background-color: ${Color.C6};
  border-radius: 8px;
  padding: 24px;
  max-width: ${MAX_WIDTH};
  margin: auto;
`;

const Detail = styled.div`
  text-align: left;
`;

const ActionWrapper = styled.ul`
  display: flex;
  justify-content: flex-end;
  list-style-type: none;
  margin: 0;
  padding: 0;
`;

const ActionItem = styled.li`
  &:not(:first-of-type) {
    margin-left: 22px;
  }
  cursor: pointer;
`;

interface DialogProps {
  content: string;
  shown: boolean;
  title?: string;
  defaultAction?: Action;
  cancelAction?: Action;
  onClose?(): void;
}

export const Dialog: React.FC<DialogProps> = ({
  content,
  shown,
  title,
  defaultAction,
  cancelAction,
  onClose,
}) => (
  <div>
    <Wrapper shown={shown} aria-hidden={!shown}>
      <Layout>
        <Content
          role="dialog"
          id="dialog"
          aria-modal
          aria-labelledby="dialog-title"
          aria-describedby="dialog-description"
          aria-expanded={shown}
        >
          <Detail role="document">
            {title ? (
              <>
                <Typography id="dialog-title" el="h2" size={FontSize.XL}>
                  {title}
                </Typography>
                <Spacer />
              </>
            ) : null}
            <div id="dialog-description">
              <Typography lineHeight="1.5rem" el="p" block>
                {content}
              </Typography>
            </div>
          </Detail>
          <Spacer />
          <footer>
            <ActionWrapper>
              {cancelAction ? (
                <ActionItem aria-controls="dialog">
                  <a onClick={cancelAction.callback}>
                    <Typography color={Color.C7}>
                      {cancelAction.label}
                    </Typography>
                  </a>
                </ActionItem>
              ) : null}
              {defaultAction ? (
                <ActionItem aria-controls="dialog">
                  <a onClick={defaultAction.callback}>
                    <Typography color={Color.C7} bold>
                      {defaultAction.label}
                    </Typography>
                  </a>
                </ActionItem>
              ) : (
                <ActionItem aria-controls="dialog">
                  <a onClick={onClose}>
                    <Typography color={Color.C7} bold>
                      <FormattedMessage id={localeIds.dialog.ok} />
                    </Typography>
                  </a>
                </ActionItem>
              )}
            </ActionWrapper>
          </footer>
        </Content>
        <Overlay aria-controls="dialog" onClick={onClose}></Overlay>
      </Layout>
    </Wrapper>
  </div>
);
